import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DetectiveIcon, SpinnerGapIcon, ArrowLeftIcon, PlusCircleIcon, CheckCircleIcon, TrophyIcon, TargetIcon, ArrowRightIcon, ChartLineUpIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fetchCabalProfile, trackCabal } from "../lib/api";
import { getClientId } from "../lib/clientId";
import { useAuth } from "../context/AuthContext";
import { QuickBuyLink } from "../components/TradeButton";
import { fmtUsd } from "../lib/format";
import { useSeo } from "../lib/useSeo";

const ROSE = "#FB7185";
const short = (a) => (a && a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");
const timeAgo = (ts) => {
  if (!ts) return "—";
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const Stat = ({ label, value, color, testid }) => (
  <div className="bg-black/30 border border-white/5 px-4 py-3 text-center" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-heading font-black text-2xl leading-tight mt-0.5" style={{ color: color || "#fff" }}>{value}</div>
  </div>
);

export default function CabalProfile() {
  const { clusterId } = useParams();
  const clientId = getClientId();
  const { requireAuth } = useAuth();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["cabalProfile", clusterId],
    queryFn: () => fetchCabalProfile(clusterId),
    refetchInterval: 60000,
  });
  useSeo({
    title: `🕵️ Cabal ${(clusterId || "").replace("cabal_", "").slice(0, 6)} — Track Record | AlphaApeing`,
    description: "The full track record of a coordinated wallet cabal — every token it's touched, entry-to-now multiples and win rate.",
  });
  const track = useMutation({
    mutationFn: () => trackCabal(clientId, clusterId),
    onSuccess: (d) => {
      if (d.added > 0) toast.success(`Tracking ${d.added} cabal wallet${d.added === 1 ? "" : "s"}`);
      else if (d.skipped > 0 && !d.capped) toast.info("You're already tracking this whole cabal 🕵️");
      else if (d.capped > 0) toast.error(`Wallet cap reached (${d.maxWallets}).`);
    },
    onError: () => toast.error("Couldn't track this cabal."),
  });
  const done = track.isSuccess && track.data?.added > 0;

  if (isLoading) return <div className="flex justify-center py-32"><SpinnerGapIcon size={32} className="animate-spin" style={{ color: ROSE }} /></div>;
  if (isError || !data) return (
    <div className="max-w-[900px] mx-auto px-6 py-24 text-center flex flex-col items-center gap-4">
      <p className="text-danger font-mono">This cabal is no longer active or hasn't been mapped.</p>
      <Link to="/cabal" className="text-neon hover:underline font-mono text-sm">← Back to Cabal Radar</Link>
    </div>
  );

  const tokens = data.tokens || [];
  const shortId = (data.clusterId || "").replace("cabal_", "").slice(0, 6);

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="cabal-profile-page">
      <Link to="/cabal" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-neon transition-colors font-mono" data-testid="cabal-profile-back">
        <ArrowLeftIcon size={16} /> Cabal Radar
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 flex items-center justify-center text-black shrink-0" style={{ background: ROSE }}>
            <DetectiveIcon size={26} weight="fill" />
          </div>
          <div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter leading-none">
              CABAL <span style={{ color: ROSE }}>{shortId}</span>
            </h1>
            <p className="text-muted-foreground text-sm mt-1 font-mono">
              {data.size} coordinated wallets · moved together across {data.sharedTokenCount} tokens
            </p>
          </div>
        </div>
        <button
          data-testid="cabal-profile-track-btn"
          onClick={() => { if (requireAuth("track this cabal")) track.mutate(); }}
          disabled={track.isPending || done}
          className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold border transition-colors disabled:opacity-70 border-[#FB7185]/50 bg-[#FB7185]/[0.08] text-[#FB7185] hover:bg-[#FB7185]/15"
        >
          {track.isPending ? <SpinnerGapIcon size={16} className="animate-spin" />
            : done ? <><CheckCircleIcon size={16} weight="bold" /> Cabal tracked</>
            : <><PlusCircleIcon size={16} weight="bold" /> Track this cabal</>}
        </button>
      </div>

      {/* stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat testid="cabal-stat-winrate" label="Win rate" value={data.winRate != null ? `${data.winRate}%` : "—"}
          color={data.winRate != null ? (data.winRate >= 50 ? "#00E599" : "#F5A623") : "#888"} />
        <Stat testid="cabal-stat-avg" label="Avg multiple" value={data.avgMultiple != null ? `${data.avgMultiple}×` : "—"} color={ROSE} />
        <Stat testid="cabal-stat-touched" label="Tokens touched" value={data.tokensTouched} />
        <Stat testid="cabal-stat-best" label="Best call" value={data.best ? `${data.best.multiple}×` : "—"} color="#F0C000" />
      </div>

      {data.ratedCount > 0 && (
        <p className="text-[11px] font-mono text-muted-foreground flex items-center gap-1.5">
          <TargetIcon size={13} style={{ color: ROSE }} /> Win rate & multiples measured on {data.ratedCount} token{data.ratedCount === 1 ? "" : "s"} bought in the last 24h (where entry price is reliably estimable). Win = ≥1.5× from entry.
        </p>
      )}

      {/* roster */}
      <div>
        <h2 className="font-heading font-bold text-sm uppercase tracking-wider text-muted-foreground mb-2">The crew ({data.size})</h2>
        <div className="flex flex-wrap gap-1.5" data-testid="cabal-roster">
          {(data.wallets || []).map((w) => (
            <a key={w} href={`https://solscan.io/account/${w}`} target="_blank" rel="noreferrer"
              className="text-[11px] font-mono px-2 py-1 border bg-[#FB7185]/10 border-[#FB7185]/30 text-[#FB7185] hover:bg-[#FB7185]/20 transition-colors">
              🕵️ {(data.walletLabels || {})[w] || short(w)}
            </a>
          ))}
        </div>
      </div>

      {/* track record */}
      <div>
        <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><TrophyIcon size={20} style={{ color: "#F0C000" }} /> Track record</h2>
        {tokens.length === 0 ? (
          <div className="border border-white/10 bg-card py-12 text-center text-muted-foreground font-mono text-sm">No tokens recorded for this cabal yet.</div>
        ) : (
          <div className="border border-white/5 divide-y divide-white/5" data-testid="cabal-token-list">
            {tokens.map((t) => (
              <div key={t.mint} data-testid={`cabal-token-${t.mint}`} className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors">
                {t.image ? (
                  <img src={t.image} alt="" className="w-9 h-9 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
                ) : (
                  <div className="w-9 h-9 flex items-center justify-center border border-[#FB7185]/40 text-[#FB7185] shrink-0"><DetectiveIcon size={16} /></div>
                )}
                <div className="min-w-0 flex-1">
                  <Link to={`/coin/${t.mint}`} className="font-heading font-bold hover:text-neon transition-colors">${t.symbol || "token"}</Link>
                  <div className="font-mono text-[11px] text-muted-foreground">
                    {t.membersIn} in · {timeAgo(t.firstTs)}
                    {t.entryMcap != null && <> · entry {fmtUsd(t.entryMcap)}</>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-sm text-white">{t.nowMcap ? fmtUsd(t.nowMcap) : "—"}</div>
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground">mcap now</div>
                </div>
                {t.multiple != null ? (
                  <div className="w-16 text-right shrink-0 font-heading font-black text-lg" style={{ color: t.multiple >= 1 ? "#00E599" : "#FF3366" }}>
                    {t.multiple}×
                  </div>
                ) : (
                  <div className="w-16 text-right shrink-0 font-mono text-xs text-muted-foreground">held &gt;24h</div>
                )}
                <div className="flex items-center gap-1 shrink-0">
                  <QuickBuyLink coin={{ address: t.mint, url: t.url }} testid={`cabal-token-buy-${t.mint}`} />
                  <Link to={`/coin/${t.mint}`} className="p-1.5 text-muted-foreground hover:text-neon transition-colors" title="Chart & Degen Score">
                    <ChartLineUpIcon size={16} weight="bold" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 text-[11px] font-mono text-muted-foreground border-t border-white/5 pt-4">
        <DetectiveIcon size={13} style={{ color: ROSE }} className="mt-0.5" />
        Track record is inferred from co-buying patterns across tracked wallets — a heuristic, not proof of collusion. Multiples for tokens bought over 24h ago aren't shown (entry price can't be reliably estimated). Always DYOR.
      </div>
    </div>
  );
}
